import {
    AutocompleteOption,
    GroupOptions,
} from "components/Form/components/Autocomplete/Autocomplete";
import { isInputValueIncluded } from "components/Form/components/Autocomplete/Autocomplete.service";
import { SimpleSelectProps } from "components/Form/components/Autocomplete/components/SimpleSelect";
import { StaticSearchMenuList } from "components/Form/components/Autocomplete/ReactSelectComponents";
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import CreatableSelect from "react-select/creatable";
import "../Autocomplete.scss";

interface StaticSelectProps extends SimpleSelectProps {
    menuRef: MutableRefObject<any>;
    defaultValue: AutocompleteOption;
    uniqueKey: string;
    components: any;
}

export const SearchableSelect = ({
    optionList,
    groupOptions,
    selectedValue,
    menuRef,
    defaultValue,
    ...restProps
}: StaticSelectProps) => {
    const isGroupOptionsList = groupOptions !== undefined;

    const allOptions = useMemo(() => {
        const optionsWithDefault = [...(optionList || [])];

        if (
            defaultValue &&
            !optionsWithDefault.find((option) => option.value === defaultValue.value)
        ) {
            optionsWithDefault.unshift(defaultValue);
        }
        return isGroupOptionsList ? groupOptions : optionsWithDefault;
    }, [optionList, defaultValue, isGroupOptionsList, groupOptions]);

    const [options, setOptions] = useState<AutocompleteOption[] | GroupOptions[]>(allOptions);
    const menuIsOpenRef = useRef(false);

    useEffect(() => {
        if (!menuIsOpenRef.current) {
            setOptions(allOptions);
        }
    }, [allOptions]);

    const filterGroupOptions = useCallback(
        (groupOptions: GroupOptions[], inputValue: string) =>
            groupOptions
                .map((group) => {
                    const options = group.options.filter((item) =>
                        isInputValueIncluded(item, inputValue)
                    );
                    return { ...group, options };
                })
                .filter((group) => group.options.length > 0),
        []
    );

    const onInputChange = (inputValue) => {
        const filteredList = inputValue?.trim()
            ? isGroupOptionsList
                ? filterGroupOptions(allOptions as GroupOptions[], inputValue)
                : (allOptions as AutocompleteOption[]).filter((item) =>
                      isInputValueIncluded(item, inputValue)
                  )
            : allOptions;

        setOptions(filteredList as AutocompleteOption[] | GroupOptions[]);
    };

    return (
        <CreatableSelect
            ref={menuRef}
            key={restProps.uniqueKey}
            options={options}
            value={selectedValue}
            onInputChange={(newValue, { action }) => {
                if (action === "input-change") {
                    onInputChange(newValue);
                } else if (action === "menu-close") {
                    menuIsOpenRef.current = false;
                    setOptions(allOptions);
                }
            }}
            onMenuOpen={() => {
                menuIsOpenRef.current = true;
            }}
            {...restProps}
            components={{
                ...restProps.components,
                MenuList: StaticSearchMenuList,
            }}
        />
    );
};
